/**
 * Client-side lat/lng -> nearest US zip code lookup.
 * No API calls, works fully offline once uszips.json is loaded.
 *
 * Data format in uszips.json: array of [zip, lat, lng, city, state]
 *
 * Usage:
 *   import { ZipLookup } from './zipLookup.js';
 *
 *   const lookup = new ZipLookup();
 *   await lookup.load('/uszips.json');
 *   const result = lookup.nearest(40.7484, -73.9857);
 *   // { zip: '10119', lat: 40.7484, lng: -73.9857, city: 'New York', state: 'NY', distanceMiles: 0.4 }
 */

const GRID_SIZE = 0.5; // degrees per grid cell, ~35 miles at mid-latitudes

function cellKey(lat, lng) {
  const gLat = Math.floor(lat / GRID_SIZE);
  const gLng = Math.floor(lng / GRID_SIZE);
  return `${gLat}:${gLng}`;
}

function haversineMiles(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

export class ZipLookup {
  constructor() {
    this.records = [];
    this.grid = new Map(); // cellKey -> array of record indices
  }

  async load(jsonUrl) {
    const res = await fetch(jsonUrl);
    if (!res.ok) throw new Error(`Failed to load zip data: ${res.status}`);
    const rows = await res.json();

    this.records = rows.map(([zip, lat, lng, city, state]) => ({
      zip,
      lat,
      lng,
      city,
      state,
    }));

    this.grid.clear();
    this.records.forEach((rec, i) => {
      const key = cellKey(rec.lat, rec.lng);
      if (!this.grid.has(key)) this.grid.set(key, []);
      this.grid.get(key).push(i);
    });
  }

  /**
   * Find the nearest zip code to a given lat/lng.
   * Searches the surrounding grid cells, expanding outward until a match
   * is found, so it doesn't need to scan all 33k records.
   */
  nearest(lat, lng) {
    if (this.records.length === 0) {
      throw new Error('ZipLookup: call load() before nearest()');
    }

    const gLat = Math.floor(lat / GRID_SIZE);
    const gLng = Math.floor(lng / GRID_SIZE);

    let best = null;
    let bestDist = Infinity;

    for (let radius = 0; radius <= 8; radius++) {
      let found = false;

      for (let dLat = -radius; dLat <= radius; dLat++) {
        for (let dLng = -radius; dLng <= radius; dLng++) {
          // only check the outer ring of this radius, inner cells already checked
          if (Math.max(Math.abs(dLat), Math.abs(dLng)) !== radius) continue;

          const key = `${gLat + dLat}:${gLng + dLng}`;
          const indices = this.grid.get(key);
          if (!indices) continue;

          found = true;
          for (const i of indices) {
            const rec = this.records[i];
            const d = haversineMiles(lat, lng, rec.lat, rec.lng);
            if (d < bestDist) {
              bestDist = d;
              best = rec;
            }
          }
        }
      }

      // Once we've found at least one candidate, do one more ring to be safe
      // (a closer point could sit just across a cell boundary), then stop.
      if (best && (found || radius > 0)) {
        if (radius > 0 && bestDist < radius * GRID_SIZE * 69) break;
      }
    }

    if (!best) return null;

    return {
      zip: best.zip,
      lat: best.lat,
      lng: best.lng,
      city: best.city,
      state: best.state,
      distanceMiles: Math.round(bestDist * 100) / 100,
    };
  }
}
